import { Link } from "react-router-dom";
import { sanitizeHtml, safeUrl } from "@/lib/sanitize";
import { styleClasses, styleInline } from "@/components/cms/builder/constants";
import CmsWidget from "@/components/cms/CmsWidget";

function ytEmbed(url = "") {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.searchParams.get("v")) return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    if (u.pathname.includes("/embed/")) return url;
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.split("/").filter(Boolean).pop()}`;
  } catch (e) { void e; }
  return url;
}

const alignCls = (a) => (a === "center" ? "text-center" : a === "right" ? "text-right" : "text-left");
const widthCls = (w) => (w === "small" ? "max-w-sm" : w === "medium" ? "max-w-2xl" : "w-full");
const padY = { none: "", sm: "py-4", md: "py-10", lg: "py-20" };
const maxW = { narrow: "max-w-3xl", normal: "max-w-5xl", wide: "max-w-7xl", full: "max-w-none" };

function ButtonBlock({ href = "#", label = "Bouton", style = "primary", align = "left" }) {
  const base = "inline-flex items-center justify-center rounded-xl px-6 py-3 font-bold transition-all";
  const cls = style === "outline"
    ? "border-2 border-[#E05638] text-[#E05638] hover:bg-[#E05638] hover:text-white"
    : "bg-[#E05638] text-white hover:brightness-110 shadow-lg shadow-orange-200/50";
  const url = safeUrl(href);
  const internal = url.startsWith("/");
  return (
    <div className={alignCls(align)}>
      {internal
        ? <Link to={url} className={`${base} ${cls}`}>{label}</Link>
        : <a href={url} target="_blank" rel="noopener noreferrer" className={`${base} ${cls}`}>{label}</a>}
    </div>
  );
}

// Rendu d'un bloc de contenu (feuille). Fonction simple (pas de composant
// auto-référent) afin d'éviter toute récursion du plugin d'édition visuelle.
function renderLeaf(block) {
  const d = block?.data || {};
  switch (block?.type) {
    case "heading": {
      const Tag = d.level === "1" ? "h1" : d.level === "3" ? "h3" : "h2";
      const size = d.level === "1" ? "text-3xl md:text-5xl" : d.level === "3" ? "text-xl md:text-2xl" : "text-2xl md:text-4xl";
      return <Tag style={d.textColor ? { color: d.textColor } : undefined} className={`font-heading font-extrabold ${d.textColor ? "" : "text-slate-900"} ${size} ${alignCls(d.align)}`}>{d.text || ""}</Tag>;
    }
    case "richtext":
      return <div style={d.textColor ? { color: d.textColor } : undefined} className={`cms-rich prose-sm max-w-none ${d.textColor ? "" : "text-slate-700"} ${alignCls(d.align)}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(d.html) }} />;
    case "image":
      return (
        <figure className={alignCls(d.align)}>
          {d.url ? <img src={d.url} alt={d.alt || ""} className={`${widthCls(d.width)} ${d.align === "center" ? "mx-auto" : d.align === "right" ? "ml-auto" : ""} rounded-2xl`} /> : null}
          {d.caption ? <figcaption className="mt-2 text-sm text-slate-500">{d.caption}</figcaption> : null}
        </figure>
      );
    case "gallery": {
      const cols = d.columns === "2" ? "sm:grid-cols-2" : d.columns === "4" ? "sm:grid-cols-4" : "sm:grid-cols-3";
      return (
        <div className={`grid grid-cols-2 ${cols} gap-3`}>
          {(d.images || []).map((im, i) => (
            <img key={i} src={im.url} alt={im.alt || ""} className="w-full h-40 object-cover rounded-xl" />
          ))}
        </div>
      );
    }
    case "button":
      return <ButtonBlock {...d} />;
    case "video":
      return d.url ? (
        <div className="aspect-video w-full overflow-hidden rounded-2xl bg-black">
          <iframe src={ytEmbed(d.url)} title="video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      ) : null;
    case "divider":
      return <hr className="border-slate-200" />;
    case "spacer":
      return <div style={{ height: `${d.size || 32}px` }} />;
    case "widget":
      return <CmsWidget widget={d.widget} />;
    default:
      return null;
  }
}

export function BlockRenderer({ block }) {
  const d = block?.data || {};
  if (block?.type === "section") {
    const cols = block.columns || [];
    if (d.free) {
      const flat = cols.flatMap((c) => c.blocks || []);
      return (
        <section style={{ ...(d.bg ? { backgroundColor: d.bg } : {}), minHeight: `${d.freeHeight || 420}px` }} className={`cms-free relative ${padY[d.paddingY || "md"] || "py-10"} ${styleClasses(d)}`}>
          <div className={`relative mx-auto px-4 sm:px-6 ${maxW[d.maxWidth || "normal"] || "max-w-5xl"}`} style={{ minHeight: `${d.freeHeight || 420}px` }}>
            {flat.map((b, i) => {
              const p = (b.data || {}).pos || { x: 5, y: 10 + i * 90, w: 60 };
              const st = { ...styleInline(b.data || {}), "--l": `${p.x || 0}%`, "--t": `${p.y || 0}px`, "--w": `${p.w || 60}%` };
              return <div key={b.id} className={`cms-free-block ${styleClasses(b.data || {})}`} style={st}>{renderLeaf(b)}</div>;
            })}
          </div>
        </section>
      );
    }
    const grid = cols.length >= 4 ? "md:grid-cols-4" : cols.length === 3 ? "md:grid-cols-3" : cols.length === 2 ? "md:grid-cols-2" : "grid-cols-1";
    return (
      <section style={d.bg ? { backgroundColor: d.bg } : undefined} className={`${padY[d.paddingY || "md"] || "py-10"} ${styleClasses(d)}`}>
        <div className={`mx-auto px-4 sm:px-6 ${maxW[d.maxWidth || "normal"] || "max-w-5xl"}`}>
          <div className={`grid grid-cols-1 ${grid} gap-6`}>
            {cols.map((c) => (
              <div key={c.id} className="space-y-5">
                {(c.blocks || []).map((b) => (
                  <div key={b.id} className={styleClasses(b.data || {})} style={styleInline(b.data || {})}>{renderLeaf(b)}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  return <div className={styleClasses(d)} style={styleInline(d)}>{renderLeaf(block)}</div>;
}

export default BlockRenderer;
